/**
 * Author: Romain Hapi Sr.
 * Version: 1.0
 * Date: 2026-06-25
 * Project: Prenim Digital
 * Copyright: Prenim UK
 */
import React from "react";
import { useLang } from "../i18n/LanguageContext";
import { Loader2, RotateCw, TriangleAlert } from "lucide-react";

/**
 * Ecran d'attente du site UK, et ecran d'echec quand il n'y a rien a attendre.
 *
 * Tant que `failed` n'existait pas, un chargement de traductions definitivement
 * rate laissait ce composant tourner indefiniment : le visiteur voyait une roue
 * sans fin, sans explication ni moyen de reessayer. La chaine de repli de
 * `LanguageContext` rend ce cas rare, mais il reste possible — coupure reseau
 * complete, deploiement ou les fichiers de langue manquent.
 *
 * Le message est en francais et en anglais cote a cote : par construction, on
 * ne dispose ici d'aucune traduction pour le rendre dans la langue du visiteur.
 */
const Loading = () => {
  const { lang, failed } = useLang();

  if (failed) {
    return (
      <div
        data-testid="page-load-failed"
        className="flex min-h-screen flex-col items-center justify-center bg-white px-6 text-center"
      >
        <TriangleAlert className="h-8 w-8 text-amber-500" />
        <p className="mt-4 max-w-md text-slate-700">
          Le contenu du site n’a pas pu être chargé.
        </p>
        <p className="mt-1 max-w-md text-sm text-slate-500">
          The site content could not be loaded.
        </p>
        <button
          type="button"
          onClick={() => window.location.reload()}
          data-testid="page-load-retry"
          className="mt-6 inline-flex items-center gap-2 rounded-full bg-blue-600 px-5 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-blue-500"
        >
          <RotateCw className="h-4 w-4" />
          Réessayer / Retry
        </button>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-white" data-testid="page-loading">
      <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
      <p className="mt-4 text-sm text-slate-500 uppercase tracking-widest">{lang}</p>
    </div>
  );
};

export default Loading;
